import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import Select from "react-select";
import SimpleDatePicker from "@/components/common/SimpleDatePicker";
import APIVariables from "@/services/api-variables";
import Axios from "@/services/Axios";
import { handleCatch } from "@/shared/helpers";

// const toPriorityLabel = (index) => {
//   // 0 -> A, 1 -> B, ... 25 -> Z, 26 -> AA, 27 -> AB, ...
//   let n = index + 1;
//   let out = "";
//   while (n > 0) {
//     n -= 1;
//     out = String.fromCharCode(65 + (n % 26)) + out;
//     n = Math.floor(n / 26);
//   }
//   return out;
// };

// const CUSTOM_FIELDS_OPTIONS = [
//   { value: "paf_id", label: "PAF ID" },
//   { value: "patient_id", label: "Patient ID" },
//   { value: "initials", label: "Initials" },
//   { value: "date_of_birth", label: "Date of Birth" },
//   { value: "molecule", label: "Molecule" },
//   { value: "capsule_strength", label: "Capsule Strength" },
//   { value: "pharmacy_dispensing", label: "Pharmacy Name (Dispensing)" },
//   { value: "pharmacy_approving", label: "Pharmacy Name (Approving)" },
// ];

// const getPriorityLetter = (selectedFields, optionValue) => {
//   const idx = selectedFields.findIndex((x) => x.value === optionValue);
//   return idx >= 0 ? toPriorityLabel(idx) : null;
// };

const ALL_OPTION = { value: null, label: "All" };
/** Products placeholder: no product filter (API receives `product: null`). */
const PRODUCT_NONE_OPTION = { value: null, label: "---" };

const normalizeMetadataMapToOptions = (obj) =>
  Object.entries(obj || {}).map(([value, label]) => ({
    value: Number(value),
    label,
  }));

const findCreatedDateTypeOption = (options) =>
  (options || []).find((opt) =>
    String(opt.label || "")
      .toLowerCase()
      .includes("created")
  ) ?? null;

/** Supports report_type as an array of objects or legacy id → name map. */
const normalizeReportTypeOptions = (reportType) => {
  if (Array.isArray(reportType)) {
    return reportType.map((item) => ({
      value: item.id,
      label: item.name,
      dateTypeAvailable: Boolean(item.date_type_available),
      productRequired: Boolean(item.product_required),
    }));
  }
  return Object.entries(reportType || {}).map(([value, label]) => ({
    value: Number(value),
    label: typeof label === "string" ? label : String(value),
    dateTypeAvailable: true,
    productRequired: false,
  }));
};

const getReportTypeName = (metadata, reportTypeId) => {
  const types = metadata?.report_type;
  if (Array.isArray(types)) {
    return (
      types.find((t) => t.id === reportTypeId)?.name ?? `Report ${reportTypeId}`
    );
  }
  return types?.[String(reportTypeId)] ?? `Report ${reportTypeId}`;
};

const toDateInputValue = (isoDateTimeOrDate) => {
  if (!isoDateTimeOrDate) return "";
  return String(isoDateTimeOrDate).slice(0, 10);
};

const formatHistoryLabel = (item, metadata) => {
  const customName =
    item?.file_name?.trim() || item?.filename?.trim() || "";
  if (customName) return customName;

  const rt = getReportTypeName(metadata, item.report_type);
  const date = toDateInputValue(item.created_at) || "";
  return `${rt}${date ? `-${date}` : ""}`;
};

function getReportingDateErrors(startDate, endDate) {
  const errors = { startDate: null, endDate: null };
  if (!startDate?.trim()) {
    errors.startDate = "Start date is required.";
  }
  if (!endDate?.trim()) {
    errors.endDate = "End date is required.";
  } else if (
    startDate?.trim() &&
    new Date(startDate) > new Date(endDate)
  ) {
    errors.endDate = "End date must be on or after start date.";
  }
  return {
    errors,
    isValid: !errors.startDate && !errors.endDate,
  };
}

const Reporting = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportMetadata, setReportMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [productOptions, setProductOptions] = useState([]);
  const [wholesalerOptions, setWholesalerOptions] = useState([ALL_OPTION]);
  const [mahOptions, setMahOptions] = useState([ALL_OPTION]);
  const [reportTypeOptions, setReportTypeOptions] = useState([]);
  const [dateTypeOptions, setDateTypeOptions] = useState([]);

  const [productId, setProductId] = useState(PRODUCT_NONE_OPTION);
  const [marketingHolderId, setMarketingHolderId] = useState(ALL_OPTION);
  const [wholesalerId, setWholesalerId] = useState(ALL_OPTION);
  const [dateType, setDateType] = useState(null);
  const [reportType, setReportType] = useState(null);
  // const [customFields, setCustomFields] = useState([]);
  const [reportName, setReportName] = useState("");
  const [exports, setExports] = useState([]);
  const [showDateValidation, setShowDateValidation] = useState(false);

  const dateValidation = useMemo(
    () => getReportingDateErrors(startDate, endDate),
    [startDate, endDate]
  );

  const productSelectOptions = useMemo(() => {
    if (reportType?.productRequired) return productOptions;
    return [PRODUCT_NONE_OPTION, ...productOptions];
  }, [productOptions, reportType?.productRequired]);

  const canSubmitReport = useMemo(() => {
    if (!dateValidation.isValid || !reportType) return false;
    if (reportType.dateTypeAvailable && !dateType) return false;
    if (reportType.productRequired && productId?.value == null) return false;
    return true;
  }, [
    dateValidation.isValid,
    reportType,
    dateType,
    productId?.value,
  ]);

  const resetForm = () => {
    const defaultReportType =
      reportTypeOptions[1] ?? reportTypeOptions[0] ?? null;
    setStartDate("");
    setEndDate("");
    setReportType(defaultReportType);
    setDateType(
      defaultReportType?.dateTypeAvailable
        ? dateTypeOptions[0] ?? null
        : findCreatedDateTypeOption(dateTypeOptions)
    );
    setProductId(
      defaultReportType?.productRequired && productOptions[0]
        ? productOptions[0]
        : PRODUCT_NONE_OPTION
    );
    setMarketingHolderId(ALL_OPTION);
    setWholesalerId(ALL_OPTION);
    // setCustomFields([]);
    setReportName("");
    setShowDateValidation(false);
  };

  const onSaveAndExport = async () => {
    if (!canSubmitReport) return;
    setShowDateValidation(true);
    if (!dateValidation.isValid) return;

    const payload = {
      product: reportType?.productRequired
        ? productId?.value ?? null
        : null,
      marketing_holder: marketingHolderId?.value ?? null,
      wholesaler: wholesalerId?.value ?? null,
      start_date: startDate || null,
      end_date: endDate || null,
      date_type: dateType?.value ?? null,
      report_type: reportType?.value ?? 0,
      file_name: reportName.trim() || null,
    };

    const res = await Axios.post(APIVariables.REPORT_HISTORY, payload).catch(
      handleCatch
    );
    if (!res) return;

    // Refresh export list to pick up the new report (incl. attach URL).
    const historyRes = await Axios.get(APIVariables.REPORT_HISTORY).catch(
      handleCatch
    );
    const list = Array.isArray(historyRes?.data) ? historyRes.data : [];
    setExports(
      list
        .map((item, idx) => ({
          id: `${item.created_at ?? "created"}-${idx}`,
          label: formatHistoryLabel(item, reportMetadata ?? {}),
          attach: item.attach,
          created_at: item.created_at,
        }))
        .sort(
          (a, b) =>
            new Date(b.created_at || 0).valueOf() -
            new Date(a.created_at || 0).valueOf()
        )
    );
    resetForm();
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const [metaRes, productsRes, wholesalersRes, historyRes] =
        await Promise.all([
          Axios.get(APIVariables.REPORT_METADATA).catch(handleCatch),
          Axios.get(APIVariables.GET_PRODUCTS).catch(handleCatch),
          Axios.get(APIVariables.ORDERS_WHOLESALERS).catch(handleCatch),
          Axios.get(APIVariables.REPORT_HISTORY).catch(handleCatch),
        ]);

      const metadata = metaRes?.data ?? null;
      setReportMetadata(metadata);
      const reportTypes = normalizeReportTypeOptions(metadata?.report_type);
      const dateTypes = normalizeMetadataMapToOptions(
        metadata?.report_date_type
      );
      const firstReportType = reportTypes[1] ?? null; // Not the "No Report" option
      setReportTypeOptions(reportTypes);
      setDateTypeOptions(dateTypes);
      setReportType(firstReportType);
      setDateType(
        firstReportType?.dateTypeAvailable
          ? dateTypes[0] ?? null
          : findCreatedDateTypeOption(dateTypes)
      );

      const products = Array.isArray(productsRes?.data) ? productsRes.data : [];
      const productOpts = products
        .filter((p) => p.product_id != null)
        .map((p) => ({
          value: p.product_id,
          label: p.name || p.product_name || `Product ${p.product_id}`,
        }));
      setProductOptions(productOpts);
      if (firstReportType?.productRequired && productOpts[0]) {
        setProductId(productOpts[0]);
      } else {
        setProductId(PRODUCT_NONE_OPTION);
      }

      const wholesalers = Array.isArray(wholesalersRes?.data)
        ? wholesalersRes.data
        : [];
      setWholesalerOptions([
        ALL_OPTION,
        ...wholesalers.map((w) => ({
          value: w.id,
          label: w.name || `Wholesaler ${w.id}`,
        })),
      ]);

      const history = Array.isArray(historyRes?.data) ? historyRes.data : [];
      setExports(
        history
          .map((item, idx) => ({
            id: `${item.created_at ?? "created"}-${idx}`,
            label: formatHistoryLabel(item, metadata),
            attach: item.attach,
            created_at: item.created_at,
          }))
          .sort(
            (a, b) =>
              new Date(b.created_at || 0).valueOf() -
              new Date(a.created_at || 0).valueOf()
          )
      );

      setIsLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    const pid = productId?.value;
    if (pid == null) {
      setMahOptions([ALL_OPTION]);
      setMarketingHolderId(ALL_OPTION);
      return;
    }

    const loadMah = async () => {
      const res = await Axios.get(`${APIVariables.MAHS}/${pid}`).catch(
        handleCatch
      );
      const list = Array.isArray(res?.data) ? res.data : [];
      setMahOptions([
        ALL_OPTION,
        ...list.map((m) => ({
          value: m.id,
          label: m.full_name || m.name || `MAH ${m.id}`,
        })),
      ]);
      setMarketingHolderId(ALL_OPTION);
    };
    loadMah();
  }, [productId?.value]);

  useEffect(() => {
    if (!reportType) return;
    if (!reportType.dateTypeAvailable) {
      setDateType(findCreatedDateTypeOption(dateTypeOptions));
    } else if (dateTypeOptions.length > 0) {
      setDateType((prev) => prev ?? dateTypeOptions[0]);
    }
  }, [reportType, dateTypeOptions]);

  useEffect(() => {
    if (!reportType) return;
    if (!reportType.productRequired) {
      setProductId(PRODUCT_NONE_OPTION);
    } else if (productOptions.length > 0) {
      setProductId((prev) => (prev?.value == null ? productOptions[0] : prev));
    }
  }, [reportType, productOptions]);

  // const CustomFieldOption = (props) => {
  //   const letter = getPriorityLetter(customFields, props.data.value);
  //   const isSelected = Boolean(letter);
  //   return (
  //     <components.Option {...props}>
  //       <div className="flex items-center justify-between">
  //         <div className="text-sm">{props.label}</div>
  //         <div
  //           className={[
  //             "flex h-9 w-9 items-center justify-center rounded-lg border text-sm font-semibold",
  //             isSelected
  //               ? "border-[#00567A] bg-[#00567A] text-white"
  //               : "border-[#00567A] bg-white text-transparent",
  //           ].join(" ")}
  //         >
  //           {letter || "•"}
  //         </div>
  //       </div>
  //     </components.Option>
  //   );
  // };

  // const onCustomFieldsChange = (_value, meta) => {
  //   const option = meta?.option;
  //   if (meta.action === "select-option" && option) {
  //     setCustomFields((prev) => {
  //       if (prev.some((x) => x.value === option.value)) return prev;
  //       return [...prev, option];
  //     });
  //     return;
  //   }

  //   if (meta.action === "remove-value" && meta.removedValue) {
  //     const removed = meta.removedValue.value;
  //     setCustomFields((prev) => prev.filter((x) => x.value !== removed));
  //     return;
  //   }

  //   if (meta.action === "clear") {
  //     setCustomFields([]);
  //     return;
  //   }

  //   // fallback (should be rare)
  //   setCustomFields(Array.isArray(_value) ? _value : []);
  // };

  return (
    <div
      className="min-h-[calc(100vh-74px)] bg-[#F2F7F6] px-6 py-8"
      data-testid="reporting-page"
    >
      <div className="mx-auto max-w-6xl">
        <div className="rounded-3xl bg-white p-8 shadow-sm">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px]">
            <div data-testid="reporting-form">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div data-testid="reporting-form-report-type">
                  <label className="mb-2 block text-sm text-slate-700">
                    Report Type
                  </label>
                  <Select
                    classNamePrefix="reporting-select"
                    value={reportType}
                    onChange={setReportType}
                    options={reportTypeOptions}
                    isLoading={isLoading}
                    inputId="reporting-report-type"
                  />
                </div>
                <div data-testid="reporting-form-date-type">
                  <label className="mb-2 block text-sm text-slate-700">
                    Date Type
                  </label>
                  <Select
                    classNamePrefix="reporting-select"
                    value={dateType}
                    onChange={setDateType}
                    options={dateTypeOptions}
                    isLoading={isLoading}
                    isDisabled={!reportType?.dateTypeAvailable}
                    inputId="reporting-date-type"
                  />
                </div>

                {/* <div>
                  <label className="mb-2 block text-sm text-slate-700">
                    Custom Report Fields (Based on Data Fields):
                  </label>
                  <Select
                    classNamePrefix="reporting-select"
                    value={customFields}
                    onChange={onCustomFieldsChange}
                    options={CUSTOM_FIELDS_OPTIONS}
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{
                      Option: CustomFieldOption,
                    }}
                  />
                </div> */}

                <div data-testid="reporting-form-start-date">
                  <SimpleDatePicker
                    id="reporting-start-date"
                    name="start_date"
                    label="Start Date"
                    required
                    value={startDate || null}
                    onChange={(d) =>
                      setStartDate(d ? format(d, "yyyy-MM-dd") : "")
                    }
                    inputClassName={[
                      "rounded-md border",
                      showDateValidation && dateValidation.errors.startDate
                        ? "border-red-500"
                        : "border-slate-300",
                    ].join(" ")}
                  />
                  {showDateValidation && dateValidation.errors.startDate ? (
                    <p
                      className="mt-1 text-xs text-red-600"
                      data-testid="reporting-form-start-date-error"
                    >
                      {dateValidation.errors.startDate}
                    </p>
                  ) : null}
                </div>
                <div data-testid="reporting-form-end-date">
                  <SimpleDatePicker
                    id="reporting-end-date"
                    name="end_date"
                    label="End Date"
                    required
                    value={endDate || null}
                    onChange={(d) =>
                      setEndDate(d ? format(d, "yyyy-MM-dd") : "")
                    }
                    inputClassName={[
                      "rounded-md border",
                      showDateValidation && dateValidation.errors.endDate
                        ? "border-red-500"
                        : "border-slate-300",
                    ].join(" ")}
                  />
                  {showDateValidation && dateValidation.errors.endDate ? (
                    <p
                      className="mt-1 text-xs text-red-600"
                      data-testid="reporting-form-end-date-error"
                    >
                      {dateValidation.errors.endDate}
                    </p>
                  ) : null}
                </div>

                <div data-testid="reporting-form-product">
                  <label className="mb-2 block text-sm text-slate-700">
                    Product:
                  </label>
                  <Select
                    classNamePrefix="reporting-select"
                    value={productId}
                    onChange={setProductId}
                    options={productSelectOptions}
                    isLoading={isLoading}
                    isClearable={false}
                    isDisabled={!reportType?.productRequired}
                    inputId="reporting-product"
                  />
                </div>
                <div data-testid="reporting-form-marketing-holder">
                  <label className="mb-2 block text-sm text-slate-700">
                    Marketing Holder:
                  </label>
                  <Select
                    classNamePrefix="reporting-select"
                    value={marketingHolderId}
                    onChange={setMarketingHolderId}
                    options={mahOptions}
                    isLoading={isLoading}
                    isDisabled={
                      !reportType?.productRequired || productId?.value == null
                    }
                    inputId="reporting-marketing-holder"
                  />
                </div>

                <div data-testid="reporting-form-wholesaler">
                  <label className="mb-2 block text-sm text-slate-700">
                    Wholesaler:
                  </label>
                  <Select
                    classNamePrefix="reporting-select"
                    value={wholesalerId}
                    onChange={setWholesalerId}
                    options={wholesalerOptions}
                    isLoading={isLoading}
                    inputId="reporting-wholesaler"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm text-slate-700">
                    Report Name
                  </label>
                  <input
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    placeholder=""
                    data-testid="reporting-form-report-name-input"
                  />
                </div>
              </div >

              <div className="mt-8 flex gap-3">
                <button
                  className="rounded-md bg-[#00567A] px-4 py-2 text-sm text-white disabled:opacity-50"
                  onClick={onSaveAndExport}
                  disabled={!canSubmitReport || isLoading}
                  data-testid="reporting-form-save-export-button"
                >
                  Save and Export
                </button>
                <button
                  className="rounded-md border border-[#00567A] px-4 py-2 text-sm text-[#00567A]"
                  onClick={resetForm}
                  data-testid="reporting-form-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div >

            <div
              className="rounded-2xl border border-slate-200 p-5"
              data-testid="reporting-export-list"
            >
              <div className="mb-3 text-sm font-semibold text-slate-800">
                Export List
              </div>
              <div className="max-h-[460px] overflow-auto pr-2">
                <ul className="space-y-3">
                  {exports.map((item) => (
                    <li key={item.id}>
                      <button
                        className="text-left text-sm text-[#1D4ED8] underline"
                        onClick={() => {
                          if (item.attach) window.open(item.attach, "_blank");
                        }}
                        data-testid={`reporting-export-item-${item.id}`}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div >
        </div >
      </div >
    </div >
  );
};

export default Reporting;

